import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import YouTubePlayer from '../../components/YouTubePlayer';
import AuctionInfo from '../../components/AuctionInfo';
import MyBid from '../../components/MyBid';
import BidHistory from '../../components/BidHistory';
import PrimaryButton from '../../components/PrimaryButton';
import AuctionTimer from '../../components/AuctionTimer';
import ImageCarousel from '../../components/ImageCarousel';
import LotDescription from '../../components/LotDescription';
import { useAuctionSocket } from '../../hooks/useAuctionSocket';
import { useAdditionalInfoCheck } from '../../hooks/useAdditionalInfoCheck';
import { BiddingRange } from '../../types/socket.types';
import { devWarn, devLog } from '../../utils/devLogger';
import LoginRequiredModal from '../../components/LoginRequiredModal';
import AdditionalInfoModal from '../../components/AdditionalInfoModal';
import BidRestrictedModal from '../../components/BidRestrictedModal';
import AuctionEndedModal from '../../components/AuctionEndedModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 증가 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 0; /* 각 컴포넌트에서 margin-bottom으로 간격 조절 */
`;

// TitleRow 스타일드 컴포넌트 (LotDetail과 동일)
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const TitleText = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

// 라이브보기 버튼 (LotDetail과 동일) - React.memo로 최적화
const LiveButton = styled.button`
  width: 89px;
  height: 28px;
  background-color: #a6d4d1;
  border: none;
  border-radius: 8px;
  color: #262626;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

// 최적화된 라이브 버튼 컴포넌트
const OptimizedLiveButton = React.memo(({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => {
  return (
    <LiveButton onClick={onClick}>
      {children}
    </LiveButton>
  );
});

// TitleRow 컴포넌트를 메모이제이션
const MemoizedTitleRow = React.memo(({ 
  title, 
  onWatchLive, 
  watchLiveText 
}: { 
  title: string; 
  onWatchLive: () => void; 
  watchLiveText: string; 
}) => {
  return (
    <TitleRow>
      <TitleText>{title}</TitleText>
      <OptimizedLiveButton onClick={onWatchLive}>
        {watchLiveText}
      </OptimizedLiveButton>
    </TitleRow>
  );
});

// 연결 상태 컴포넌트를 메모이제이션
const MemoizedConnectionStatus = React.memo(({ 
  isConnected, 
  statusText 
}: { 
  isConnected: boolean; 
  statusText: string; 
}) => {
  return (
    <ConnectionStatus $isConnected={isConnected}>
      {statusText}
    </ConnectionStatus>
  );
});

// 고정 버튼 영역을 메모이제이션 - 소켓 상태와 독립적
const MemoizedFixedButtonContainer = React.memo(({ 
  onClick, 
  buttonText
}: { 
  onClick: () => void; 
  buttonText: string;
}) => {
  
  // 하단 네비게이션과 동일한 구조로 만든 고정 버튼 컨테이너
  const FixedButtonContainer = styled.div`
    position: fixed;
    bottom: 75px; /* 앱에서 하단 네비게이션 위로 */
    left: 0;
    right: 0;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    z-index: 999; /* 하단 네비게이션(1000)보다 살짝 낮게 */
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1); /* 쉐도우 추가 */

    @media (min-width: 768px) {
      bottom: 0; /* 웹에서는 항상 바닥에 고정 */
      padding: 20px 0;
      justify-content: center; /* 웹에서 중앙 정렬 */
    }
  `;

  // 웹용 컨테이너 (1200px 제한)
  const WebContainer = styled.div`
    width: 100%;
    
    @media (min-width: 768px) {
      max-width: 1200px;
      display: flex;
      justify-content: flex-end; /* 1200px 컨테이너 내에서 오른쪽 정렬 */
      padding: 0 24px;
    }
  `;

  // 버튼 래퍼
  const ButtonWrapper = styled.div`
    width: 100%;

    @media (min-width: 768px) {
      width: 390px;
      max-width: 390px;
    }
  `;

  return (
    <FixedButtonContainer>
      <WebContainer>
        <ButtonWrapper>
          <PrimaryButton 
            onClick={onClick}
            // disabled 제거 - 항상 활성화
          >
            {buttonText}
          </PrimaryButton>
        </ButtonWrapper>
      </WebContainer>
    </FixedButtonContainer>
  );
});

// LOT 상세 섹션 헤더
const LotDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 24px;
  cursor: pointer;
`;

// 더보기/접기 버튼
const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
  
  &:hover {
    color: #333;
  }
`;

// 접을 수 있는 콘텐츠 컨테이너
const CollapsibleContent = styled.div<{ $isCollapsed: boolean }>`
  display: ${props => props.$isCollapsed ? 'none' : 'block'};
  margin-bottom: ${props => props.$isCollapsed ? '0' : '24px'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

// 웹용 LOT 상세 컨텐츠 (좌우 배치)
const DesktopLotContent = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  
  > * {
    flex: 1;
    min-width: 0; /* flex 컨테이너에서 overflow 방지 */
    max-width: 50%; /* 각각 최대 50%로 제한 */
  }
`;

// 이미지 캐로셀 래퍼 (웹용)
const ImageCarouselWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

// 웹용 LOT 상세 타이틀
const DesktopLotTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 16px 0;
`;

// 웹용 입찰 섹션 컨테이너 (나의 입찰 + 입찰 내역)
const DesktopBidSection = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;

// 나의 입찰 래퍼 (고정 너비 + 웹에서 배경색 + 고정 높이)
// 경매 타이머 래퍼 (웹용)
const AuctionTimerWrapper = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const MyBidWrapper = styled.div<{ $hasBid: boolean }>`
  width: 350px;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    background: ${props => props.$hasBid ? '#f8f5f0' : '#f3f6f6'};
    border-radius: 12px;
    padding: 20px;
    height: 120px; /* 웹에서 고정 높이 설정 */
  }
`;

// 입찰 내역 래퍼 (나머지 공간)
const BidHistoryWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

// 연결 상태 표시 컴포넌트
const ConnectionStatus = styled.div<{ $isConnected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${props => props.$isConnected ? '#e8f5e8' : '#ffeaa7'};
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: ${props => props.$isConnected ? '#2d7738' : '#b8860b'};
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.$isConnected ? '#4caf50' : '#ff9800'};
  }
`;

// 에러 메시지 컴포넌트
const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorCloseButton = styled.button`
  background: none;
  border: none;
  color: #c62828;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    opacity: 0.7;
  }
`;

// 경매 종료 모달 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const ModalMessage = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 48px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:active {
    background-color: #004085;
  }
`;

// 로딩 상태 컴포넌트
const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  color: #666;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-top: 2px solid #666;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 12px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const liveBidTexts = {
  ko: {
    title: '라이브 입찰',
    bidButton: '라이브 입찰하기',
    bid: '입찰', // 입찰 텍스트 추가
    liveAuction: '라이브경매',
    watchLive: '라이브보기',
    showMore: '더보기', // 더보기 텍스트 추가
    showLess: '접기', // 접기 텍스트 추가
    lotDetail: 'LOT 상세', // LOT 상세 텍스트 추가
    connected: '라이브 연결됨',
    connecting: '연결 중...',
    disconnected: '연결 끊김',
    noAuctionData: '경매 정보를 불러오는 중...',
    retry: '다시 시도',
    missingParams: '경매 정보가 누락되었습니다. 다시 시도해주세요.',
    goBack: '이전으로'
  },
  en: {
    title: 'Live Bid',
    bidButton: 'Place Live Bid',
    bid: 'Bid', // 입찰 텍스트 추가
    liveAuction: 'Live Auction',
    watchLive: 'Watch Live',
    showMore: 'Show More', // 더보기 텍스트 추가
    showLess: 'Show Less', // 접기 텍스트 추가
    lotDetail: 'LOT Details', // LOT 상세 텍스트 추가
    connected: 'Live Connected',
    connecting: 'Connecting...',
    disconnected: 'Disconnected',
    noAuctionData: 'Loading auction data...',
    retry: 'Retry',
    missingParams: 'Auction information is missing. Please try again.',
    goBack: 'Go Back'
  }
};

function LiveBid() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const { user } = useUserAuth();
  const texts = liveBidTexts[lang];
  const [isLotDetailCollapsed, setIsLotDetailCollapsed] = useState(true);
  const [showAuctionEndModal, setShowAuctionEndModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBidRestrictedModal, setShowBidRestrictedModal] = useState(false);

  // 추가 정보 체크 훅
  const additionalInfoCheck = useAdditionalInfoCheck({
    onNavigate: () => navigate('/additional-info')
  });

  // 소켓 상태를 ref로 저장하여 리렌더링과 독립적으로 관리
  const socketStateRef = useRef({
    isConnected: false,
    auctionData: null as any
  });

  // location.state로부터 파라미터 추출
  const { auctionIndex: paramAuctionIndex, lotIndex: paramLotIndex, selectedItem } = location.state || {};

  // selectedItem 구조 분석
  const auctionIndex = paramAuctionIndex || 
                      selectedItem?.apiData?.auction?.index || 
                      selectedItem?.apiData?.auction?.id ||
                      selectedItem?.auctionIndex || 
                      selectedItem?.auctionId;
                      
  const lotIndex = paramLotIndex || 
                  selectedItem?.apiData?.index || 
                  selectedItem?.id ||
                  selectedItem?.lotIndex || 
                  selectedItem?.lotId;
  
  // 소켓 훅 사용 - 자동 데이터 요청 비활성화하여 중복 호출 방지
  const {
    isConnected,
    auctionData,
    bidHistory,
    countdownStatus, // 카운트다운 상태 추가
    currentLot, // 소켓 이벤트로 관리되는 현재 진행 중인 lot
    isAuctionEnded, // 소켓 이벤트로 관리되는 경매 종료 상태
    isBanned, // 사용자 차단 상태
    // error,
    getAuctionInfo,
    getBidHistory,
    // clearError,
    // clearBidHistory
  } = useAuctionSocket({
    auctionIndex,
    autoConnect: true,
    autoRequestData: false // 자동 데이터 요청 비활성화 - 컴포넌트에서 직접 제어
  });

  // 현재 표시할 lot을 가져오는 유틸리티 함수 - 소켓 이벤트 우선
  const getCurrentLot = useCallback((lots: any[]) => {
    // 1. 소켓 이벤트로 받은 currentLot이 있으면 우선 사용
    if (currentLot && currentLot.lotIndex) {
      const socketLot = lots.find(lot => lot.index === currentLot.lotIndex);
      if (socketLot) {
        devLog('🎯 [LiveBid] 소켓 이벤트로 받은 현재 lot 사용:', {
          lotIndex: currentLot.lotIndex,
          lotNo: currentLot.lotNo,
          lotName: currentLot.lotName
        });
        return socketLot;
      }
    }
    
    // 2. 소켓 이벤트 정보가 없으면 기존 로직 사용 (fallback)
    if (!lots || lots.length === 0) return null;
    
    // disabled가 true인 로트들은 제외하고, 유효한 로트만 필터링
    const enabledLots = lots.filter(lot => !lot.disabled);
    
    // disabled가 false인 lot 중에서 ended가 false인 lot 찾기
    const availableLots = enabledLots.filter(lot => !lot.ended);
    const fallbackLot = availableLots.length > 0 
      ? availableLots.sort((a, b) => a.lotNo - b.lotNo)[0]
      : enabledLots[0]; // disabled가 false인 lot이 모두 ended면 첫 번째 enabled lot 사용
    
    devLog('🎯 [LiveBid] fallback으로 기존 로직 사용:', {
      lotIndex: fallbackLot?.index,
      lotNo: fallbackLot?.lotNo,
      reason: '소켓 이벤트 정보 없음'
    });
    
    return fallbackLot;
  }, [currentLot]); // currentLot 의존성 추가

  // getAuctionInfo, getBidHistory 함수를 ref로 저장하여 최신 함수 참조 유지
  const getAuctionInfoRef = useRef(getAuctionInfo);
  getAuctionInfoRef.current = getAuctionInfo;
  
  const getBidHistoryRef = useRef(getBidHistory);
  getBidHistoryRef.current = getBidHistory;



  // 컴포넌트 마운트 시 경매 정보 요청 - 중복 호출 방지를 위해 ref 사용
  const hasRequestedAuctionInfo = useRef(false);
  const lastRequestedAuctionIndex = useRef<number | null>(null);
  
  useEffect(() => {
    if (isConnected && auctionIndex) {
      // 이미 같은 auctionIndex로 요청했으면 스킵
      if (hasRequestedAuctionInfo.current && lastRequestedAuctionIndex.current === auctionIndex) {
        devLog('🏛️ [LiveBid] 경매 정보 요청 스킵 (이미 요청함):', {
          auctionIndex,
          lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
        });
        return;
      }
      
      devLog('🏛️ [LiveBid] 경매 정보 요청 (최초 또는 auctionIndex 변경):', {
        auctionIndex,
        lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
      });
      
      hasRequestedAuctionInfo.current = true;
      lastRequestedAuctionIndex.current = auctionIndex;
      getAuctionInfoRef.current(auctionIndex);
    }
    
    // 연결이 끊어지면 플래그 리셋
    if (!isConnected) {
      hasRequestedAuctionInfo.current = false;
      lastRequestedAuctionIndex.current = null;
    }
  }, [isConnected, auctionIndex]); // getAuctionInfo 의존성 제거

  // 현재 표시되는 lot을 추적하여 lot 변경시 새로운 입찰 내역 로드
  const hasRequestedBidHistory = useRef(false);
  const lastRequestedLotIndex = useRef<number | null>(null);
  const currentDisplayedLotIndex = useRef<number | null>(null);
  
  // 현재 표시할 lot의 index를 계산하는 메모이제이션
  const currentLotIndex = useMemo(() => {
    // 소켓 이벤트로 받은 currentLot이 있으면 우선 사용
    if (currentLot && currentLot.lotIndex) {
      devLog('🎯 [LiveBid] 소켓 이벤트 currentLot 사용:', {
        lotIndex: currentLot.lotIndex,
        lotNo: currentLot.lotNo,
        lotName: currentLot.lotName
      });
      return currentLot.lotIndex;
    }
    
    // fallback: 기존 로직
    if (!auctionData?.lots || auctionData.lots.length === 0) {
      devLog('🎯 [LiveBid] currentLotIndex 계산: 경매 데이터 없음');
      return null;
    }
    const currentLotFromData = getCurrentLot(auctionData.lots);
    const lotIndex = currentLotFromData?.index || null;
    
    devLog('🎯 [LiveBid] currentLotIndex 계산 (fallback):', {
      전체로트수: auctionData.lots.length,
      현재로트인덱스: lotIndex,
      현재로트번호: currentLotFromData?.lotNo,
      현재로트종료여부: currentLotFromData?.ended,
      현재로트비활성화여부: currentLotFromData?.disabled,
      전체로트상태: auctionData.lots.map(lot => ({
        index: lot.index,
        lotNo: lot.lotNo,
        ended: lot.ended,
        disabled: lot.disabled
      }))
    });
    
    return lotIndex;
  }, [auctionData?.lots, getCurrentLot, currentLot]); // currentLot 의존성 추가

  // 모든 lot이 종료되었는지 확인하는 메모이제이션 - 소켓 이벤트 기반
  const allLotsEnded = useMemo(() => {
    // 소켓 이벤트로 받은 경매 종료 상태 우선 사용
    if (isAuctionEnded) {
      devLog('🔍 [LiveBid] 소켓 이벤트로 경매 종료 확인됨');
      return true;
    }
    
    // 기존 로직은 fallback으로 유지
    if (!auctionData?.lots || auctionData.lots.length === 0) return false;
    
    // disabled가 false인 lot 중에서 ended가 false인 lot이 있는지 확인
    const enabledLots = auctionData.lots.filter(lot => !lot.disabled);
    const activeLotsCount = enabledLots.filter(lot => !lot.ended).length;
    
    const allEnded = activeLotsCount === 0 && enabledLots.length > 0;
    
    devLog('🔍 [LiveBid] fallback 경매 종료 상태 확인:', {
      전체로트수: auctionData.lots.length,
      활성로트수: enabledLots.length,
      진행중로트수: activeLotsCount,
      모든로트종료: allEnded
    });
    
    return allEnded;
  }, [auctionData?.lots, isAuctionEnded]); // isAuctionEnded 의존성 추가

  // 모달 관련 핸들러들
  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  // 좋아요 기능을 위한 추가정보 모달 처리
  const handleAdditionalInfoRequired = () => {
    additionalInfoCheck.checkAndNavigate();
  };
  
  useEffect(() => {
    // 경매 데이터가 있고, 유효한 lots가 있을 때만 입찰 내역 조회
    if (auctionData && auctionData.lots && auctionData.lots.length > 0 && currentLotIndex) {
      const currentLot = getCurrentLot(auctionData.lots);
      
      if (!currentLot) {
        devWarn('📚 [LiveBid] 현재 표시할 lot을 찾을 수 없음');
        return;
      }
      
      // lot이 변경되었는지 확인 (ended가 true가 되어 다음 lot으로 넘어간 경우)
      const isLotChanged = currentDisplayedLotIndex.current !== currentLotIndex;
      
      devLog('🔍 [LiveBid] 입찰 내역 요청 조건 체크:', {
        hasAuctionData: !!auctionData,
        lotsCount: auctionData.lots.length,
        currentLotIndex,
        currentLotNo: currentLot?.lotNo,
        currentDisplayedLotIndex: currentDisplayedLotIndex.current,
        isLotChanged,
        hasRequestedBidHistory: hasRequestedBidHistory.current,
        lastRequestedLotIndex: lastRequestedLotIndex.current
      });
      
      if (isLotChanged) {
        devLog('🔄 [LiveBid] LOT 변경 감지! 이전:', currentDisplayedLotIndex.current, '→ 현재:', currentLotIndex);
        // lot이 변경되었으므로 입찰 내역을 새로 불러와야 함
        hasRequestedBidHistory.current = false;
        lastRequestedLotIndex.current = null;
        currentDisplayedLotIndex.current = currentLotIndex;
        
        // 기존 입찰 내역 클리어 (새로운 lot의 입찰 내역을 깨끗하게 보여주기 위해)
        devLog('�️ [LiveBid] 기존 입찰 내역 클리어 (새 LOT으로 전환)');
        // clearBidHistory(); // 주석 처리: 입찰 내역은 유지하되 새로운 데이터로 교체
      }
      
      // 이미 같은 lotIndex로 요청했으면 스킵 (중복 호출 방지)
      if (hasRequestedBidHistory.current && lastRequestedLotIndex.current === currentLotIndex && !isLotChanged) {
        devLog('📚 [LiveBid] 입찰 내역 조회 스킵 (이미 요청함):', {
          auctionIndex,
          currentLotIndex,
          currentLotNo: currentLot?.lotNo,
          lastRequestedLotIndex: lastRequestedLotIndex.current,
          isLotChanged
        });
        return;
      }
      
      devLog('📚 [LiveBid] 입찰 내역 조회 (현재 표시되는 lot):', {
        auctionIndex,
        currentLotIndex,
        currentLotNo: currentLot?.lotNo,
        currentLotEnded: currentLot?.ended,
        lastRequestedLotIndex: lastRequestedLotIndex.current,
        isLotChanged,
        reason: isLotChanged ? 'LOT 변경됨' : '초기 로딩'
      });
      
      hasRequestedBidHistory.current = true;
      lastRequestedLotIndex.current = currentLotIndex;
      
      // lot 변경된 경우 약간의 지연 후 요청 (서버에서 처리가 완료된 후)
      const delay = isLotChanged ? 200 : 0;
      setTimeout(() => {
        getBidHistoryRef.current(auctionIndex, currentLotIndex);
      }, delay);
    } else if (auctionData) {
      devWarn('⚠️ 경매 데이터는 있지만 lots가 없거나 currentLotIndex가 없음:', {
        hasAuctionData: !!auctionData,
        lotsCount: auctionData?.lots?.length || 0,
        currentLotIndex
      });
    }
    
    // 경매 데이터가 없어지면 플래그 리셋
    if (!auctionData) {
      hasRequestedBidHistory.current = false;
      lastRequestedLotIndex.current = null;
      currentDisplayedLotIndex.current = null;
    }
  }, [auctionData, auctionIndex, currentLotIndex, getCurrentLot]); // currentLotIndex 의존성 추가로 lot 변경시 자동 감지

  // 모든 lot이 종료되었을 때 모달 표시
  useEffect(() => {
    if (allLotsEnded && !showAuctionEndModal) {
      devLog('🏁 [LiveBid] 모든 경매 아이템 입찰 종료 - 모달 표시');
      setShowAuctionEndModal(true);
    }
  }, [allLotsEnded, showAuctionEndModal]);

  // 이미지 URL 처리 함수 (PreBid와 동일)
  const getImageSrc = useCallback((imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
     
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  }, []);

  // ImageCarousel에 전달할 이미지 데이터 준비
  const imageData = useMemo(() => {
    if (auctionData?.lots && auctionData.lots.length > 0) {
      const currentLot = getCurrentLot(auctionData.lots);
        
      if (currentLot?.images && currentLot.images.length > 0) {
        return currentLot.images.map((imageUrl: string) => getImageSrc(imageUrl));
      }
    }
    return [];
  }, [auctionData?.lots, getImageSrc, getCurrentLot]);

  // AuctionInfo에 전달할 데이터 변환 (소켓 데이터만 사용)
  const auctionDisplayData = useMemo(() => {
    if (!auctionData) {
      devWarn('🏛️ 소켓 경매 데이터 없음');
      return undefined;
    }
    
    
    // ended가 false인 lot 중에서 가장 작은 lotNo를 가진 lot 찾기
    const currentLot = getCurrentLot(auctionData.lots || []);
    
    
    return {
      auctionHouse: auctionData.auctioneer?.name || '경매사명',
      auctioneerIndex: auctionData.auctioneer?.index || 0, // auctioneerIndex 추가
      auction: auctionData.name || '경매명',
      lotName: currentLot?.lotName || '작품명',
      lotNo: currentLot?.lotNo || 1,
      code: currentLot?.lotCode || `LOT.${currentLot?.lotNo || 1}`, // 서버 제공 lotCode 우선 사용
      index: currentLot?.index || 0, // 첫 번째 lot의 index 추가
      category: currentLot?.category?.krName || currentLot?.category?.name || '카테고리',
      subCategory: currentLot?.subCategory?.krName || currentLot?.subCategory?.name || '서브카테고리',
      liveStartTime: auctionData.auctionStartAt,
      liveEndTime: auctionData.auctionEndAt,
      preBidStartTime: auctionData.preBidStartAt,
      preBidEndTime: auctionData.preBidEndAt,
      paymentDueAt: auctionData.paymentDueAt,
      currentPrice: auctionData.currentPrice || currentLot?.startPrice,
      startPrice: currentLot?.startPrice || 0,
      startPriceUsd: currentLot?.startPriceUsd || 0,
      liveStartPrice: auctionData.preBids && auctionData.preBids.length > 0 
        ? Math.max(...auctionData.preBids.map((bid: any) => bid.price || 0))
        : currentLot?.startPrice || 0, // preBids 최고가 우선, 없으면 startPrice
      exchangeRate: currentLot?.exchangeRate || 1,
      participants: auctionData.participants || 0,
      status: auctionData.status,
      auctionFeeRate: auctionData.auctionFeeRate || 0,
      platformFeeRate: auctionData.platformFeeRate || 0,
      biddingRanges: auctionData.biddingRanges || [],
      images: currentLot?.images || [],
      likeCount: currentLot?.likeCount || 0,
      isLiked: user?.index ? currentLot?.likedUserIndexes?.includes(user.index) || false : false,
      url: auctionData.url,
      description: auctionData.description,
      auctioneerInfo: {
        name: auctionData.auctioneer?.name,
        description: auctionData.auctioneer?.description,
        profilePic: auctionData.auctioneer?.profile_pic,
        term: auctionData.auctioneer?.term
      }
    };
  }, [auctionData, user?.index, getCurrentLot]);

  // 소켓 상태를 변화 감지
  useEffect(() => {
    socketStateRef.current.isConnected = isConnected;
    socketStateRef.current.auctionData = auctionData;
  }, [isConnected, auctionData]);

  const handleBidRequest = useCallback(() => {
    // 로그인 상태 확인 - 로그인하지 않은 경우 모달 띄우기
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    // 차단된 사용자인지 확인
    if (isBanned) {
      setShowBidRestrictedModal(true);
      return;
    }
    
    // 클릭 시점에만 현재 상태를 확인 (렌더링과 독립적)
    const currentIsConnected = socketStateRef.current.isConnected;
    const currentAuctionData = socketStateRef.current.auctionData;
    
    
    // 클릭 시점에 소켓 상태 확인
    if (!currentIsConnected) {
      alert('소켓 연결이 필요합니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    
    if (!currentAuctionData) {
      alert('경매 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    
    // 현재 표시되고 있는 로트의 index를 전달
    const currentLot = getCurrentLot(currentAuctionData.lots || []);
    const currentLotIndex = currentLot?.index;
    
    if (!currentLotIndex) {
      alert('현재 진행 중인 로트 정보를 찾을 수 없습니다.');
      return;
    }
    
    // 입찰 요청 시 소켓 데이터와 현재 로트 인덱스 전달
    navigate('/live-bid-request', { 
      state: { 
        auctionData: currentAuctionData,
        auctionIndex,
        lotIndex: currentLotIndex // 현재 표시되고 있는 로트의 index 전달
      } 
    });
  }, [navigate, auctionIndex, getCurrentLot, user, isBanned]); // isBanned 의존성 추가

  const handleWatchLive = useCallback(() => {
    // 소켓에서 받아온 URL을 새 페이지에서 열기
    if (auctionData?.url) {
      window.open(auctionData.url, '_blank');
    } else {
      devWarn('🔗 [LiveBid] 라이브 URL이 없습니다:', auctionData);
      // URL이 없을 경우 사용자에게 알림
      alert('라이브 스트림 URL을 불러올 수 없습니다.');
    }
  }, [auctionData]);

  const toggleLotDetail = useCallback(() => {
    setIsLotDetailCollapsed(!isLotDetailCollapsed);
  }, [isLotDetailCollapsed]);

  const handleAuctionEndConfirm = useCallback(() => {
    devLog('🏠 [LiveBid] 경매 종료 - 메인 페이지로 이동');
    navigate('/', { replace: true }); // replace로 뒤로가기 방지
  }, [navigate]);

  // 입찰 내역을 BidHistory 컴포넌트에서 사용할 수 있는 형태로 변환
  const transformedBidHistory = useMemo(() => {
    return bidHistory.map((bid) => ({
      bidNumber: bid.code, // 익명 코드를 입찰 번호로 사용
      amount: bid.price,
      isMyBid: user?.index && bid.user?.index ? user.index === bid.user.index : false // 현재 사용자의 입찰인지 확인 (null-safe)
    }));
  }, [bidHistory, user?.index]);

  // 내 입찰 정보 가져오기 (현재 사용자의 가장 최근 입찰)
  const myBidInfo = useMemo(() => {
    if (bidHistory.length > 0 && user?.index) {
      // 현재 사용자의 입찰 중 가장 최근 것 찾기 (user가 존재하는 입찰만 필터링)
      const myBids = bidHistory.filter(bid => bid.user?.index && bid.user.index === user.index);
      if (myBids.length > 0) {
        const latestMyBid = myBids[0]; // 이미 가격순으로 정렬되어 있음
        return {
          bidNumber: latestMyBid.code,
          bidAmount: latestMyBid.price,
          hasBid: true
        };
      }
    }
    return {
      hasBid: false
    };
  }, [bidHistory, user?.index]);

  // 최소 입찰가 계산 함수 (LiveBidRequest와 동일한 로직)
  const calculateMinimumBidPrice = useCallback(() => {
    if (!auctionData?.biddingRanges || !auctionData?.lots || auctionData.lots.length === 0) {
      return null;
    }

    const currentLot = getCurrentLot(auctionData.lots);
    if (!currentLot) return null;

    // 숫자 검증 함수
    const safeNumber = (value: any, defaultValue: number = 0): number => {
      const num = Number(value);
      return isNaN(num) || !isFinite(num) ? defaultValue : num;
    };

    const startPrice = safeNumber(currentLot.startPrice, 0);
    let maxBidPrice = startPrice; // 기본값은 시작가

    // bidHistory에서 현재 로트에 해당하는 가장 높은 입찰가 찾기
    if (bidHistory && bidHistory.length > 0) {
      const targetLotIndex = currentLot.index || lotIndex;
      
      // 현재 로트에 해당하는 입찰들만 필터링
      const currentLotBids = bidHistory.filter(bid => bid.lot === targetLotIndex);
      
      if (currentLotBids.length > 0) {
        // 가장 높은 입찰가 찾기
        const highestBid = currentLotBids.reduce((max, bid) => {
          const bidPrice = safeNumber(bid.price, 0);
          return bidPrice > max ? bidPrice : max;
        }, 0);
        
        if (highestBid > 0) {
          maxBidPrice = highestBid;
        }
      }
    }

    // maxBidPrice가 유효하지 않으면 기본값 사용
    if (!isFinite(maxBidPrice) || maxBidPrice <= 0) {
      maxBidPrice = 100000; // 기본 시작가 10만원
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = auctionData.biddingRanges;
    let currentRange = biddingRanges.find((range: BiddingRange) => {
      const minAmount = safeNumber(range.startPrice, 0);
      const maxAmount = range.endPrice !== null ? safeNumber(range.endPrice, 0) : null;
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 endPrice와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex((range: BiddingRange) => 
        range.endPrice !== null && maxBidPrice === safeNumber(range.endPrice, 0)
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 아예 없는 경우 기본값 반환
        return maxBidPrice + 1000;
      }
    }

    // 최소 입찰가: 최대 금액 + increment
    const increment = safeNumber(currentRange.increment, 1000);
    const minimumBidPrice = maxBidPrice + increment;

    return minimumBidPrice;
  }, [auctionData, bidHistory, lotIndex, getCurrentLot]);

  // 최소 입찰가 (메모이제이션)
  const minimumBidPrice = useMemo(() => {
    return calculateMinimumBidPrice();
  }, [calculateMinimumBidPrice]);

  // 현재가 계산 (최고 입찰가 또는 시작가)
  const currentPrice = useMemo(() => {
    if (!auctionData?.lots || auctionData.lots.length === 0) return 1000000;
    
    const currentLot = getCurrentLot(auctionData.lots);
    if (!currentLot) return 1000000;

    // 입찰 내역에서 현재 로트의 최고가 찾기
    if (bidHistory && bidHistory.length > 0) {
      const currentLotBids = bidHistory.filter(bid => bid.lot === currentLot.index);
      if (currentLotBids.length > 0) {
        const highestBid = Math.max(...currentLotBids.map(bid => bid.price));
        return highestBid;
      }
    }
    
    // 입찰이 없으면 시작가 반환
    return auctionData.currentPrice || currentLot.startPrice || 1000000;
  }, [auctionData, bidHistory, getCurrentLot]);

  // 버튼 텍스트 (통화 코드 포함 + 입찰)
  const bidButtonText = useMemo(() => {
    if (!minimumBidPrice) {
      return texts.bidButton; // 기본 버튼 텍스트
    }

    // 언어별 통화 표시 (소켓에서 받은 환율 정보 사용)
    const currentLot = auctionData?.lots ? getCurrentLot(auctionData.lots) : null;
    const exchangeRate = currentLot?.exchangeRate || 1409.09;

    if (lang === 'ko') {
      return `KRW ${minimumBidPrice.toLocaleString()} ${texts.bid}`;
    } else {
      const usdPrice = Math.round((minimumBidPrice / exchangeRate) * 100) / 100;
      return `USD ${usdPrice.toFixed(2)} ${texts.bid}`;
    }
  }, [minimumBidPrice, texts.bidButton, texts.bid, lang, auctionData, getCurrentLot]);

  // 입찰 버튼 props를 메모이제이션 - 소켓 상태와 독립적
  const bidButtonProps = useMemo(() => ({
    onClick: handleBidRequest,
    buttonText: bidButtonText
  }), [handleBidRequest, bidButtonText]);

  // 연결 상태 텍스트 (현재 UI에서 사용하지 않음)
  // const connectionStatusText = useMemo(() => {
  //   if (isConnected) return texts.connected;
  //   return texts.connecting;
  // }, [isConnected, texts.connected, texts.connecting]);

  // 필수 파라미터가 없으면 에러 화면 표시
  if (!auctionIndex) {
    return (
      <MainLayout currentPage="home">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="home"
            title={texts.title}
            showBackButton={true}
            onBack={() => navigate(-1)}
          />
          <Container>
            <ContentContainer>
              <ErrorMessage>
                <span>{texts.missingParams}</span>
                <PrimaryButton onClick={() => navigate(-1)}>
                  {texts.goBack}
                </PrimaryButton>
              </ErrorMessage>
            </ContentContainer>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
  }

  const MobileLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 연결 상태 표시 */}
            {/* <MemoizedConnectionStatus 
              isConnected={isConnected}
              statusText={connectionStatusText}
            /> */}

            {/* 에러 메시지 표시 */}
            {/* {error && (
              <ErrorMessage>
                <span>{error}</span>
                <ErrorCloseButton onClick={clearError}>×</ErrorCloseButton>
              </ErrorMessage>
            )} */}

            <MemoizedTitleRow 
              title={texts.liveAuction}
              onWatchLive={handleWatchLive}
              watchLiveText={texts.watchLive}
            />
            {auctionData?.url && (
              <YouTubePlayer youtubeUrl={auctionData.url} />
            )}
            
            {/* LOT 상세 섹션 */}
            <LotDetailHeader onClick={toggleLotDetail}>
              <FormTitle>{texts.lotDetail}</FormTitle>
              <ToggleButton>
                {isLotDetailCollapsed ? texts.showMore : texts.showLess}
              </ToggleButton>
            </LotDetailHeader>
            
            <CollapsibleContent $isCollapsed={isLotDetailCollapsed}>
              {!auctionData ? (
                <LoadingMessage>
                  {texts.noAuctionData}
                </LoadingMessage>
              ) : (
                <>
                  <ImageCarousel 
                    images={imageData}
                    lotId={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return undefined;
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.index;
                    })()}
                    likedUserIndexes={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return undefined;
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.likedUserIndexes;
                    })()}
                    onFavoriteUpdate={() => {}} // 소켓 데이터이므로 업데이트 안함
                    onLoginRequired={() => setShowLoginModal(true)}
                    onAdditionalInfoRequired={handleAdditionalInfoRequired}
                  />
                  <AuctionInfo 
                    auctionData={auctionDisplayData} 
                    showLiveStartPrice={true}
                  />
                  <LotDescription 
                    isVisible={!isLotDetailCollapsed} 
                    description={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return '';
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.description || '';
                    })()} 
                  />
                </>
              )}
            </CollapsibleContent>
         
            <div style={{ marginTop: '24px' }}>
              <AuctionTimer 
                currentPrice={currentPrice}
                exchangeRate={(() => {
                  if (!auctionData?.lots || auctionData.lots.length === 0) return 1300;
                  const currentLot = getCurrentLot(auctionData.lots);
                  return currentLot?.exchangeRate || 1300;
                })()}
                countdownStatus={countdownStatus} // 소켓에서 받은 카운트다운 상태 전달
                onTimerClick={() => devLog('Timer clicked')}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <MyBid {...myBidInfo} />
            </div>
            <div style={{ marginTop: '16px' }}>
              <BidHistory bidHistory={transformedBidHistory} />
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 - 모바일 */}
          <MemoizedFixedButtonContainer
            onClick={bidButtonProps.onClick}
            buttonText={bidButtonProps.buttonText}
          />
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  const DesktopLayout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>

            {/* LOT 상세 섹션 - 웹에서는 맨 위에 배치, 항상 표시 */}
            <DesktopLotTitle>{texts.lotDetail}</DesktopLotTitle>
            
            {!auctionData ? (
              <LoadingMessage>
                {texts.noAuctionData}
              </LoadingMessage>
            ) : (
              <DesktopLotContent>
                <ImageCarouselWrapper>
                  <ImageCarousel 
                    images={imageData}
                    lotId={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return undefined;
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.index;
                    })()}
                    likedUserIndexes={(() => {
                      if (!auctionData?.lots || auctionData.lots.length === 0) return undefined;
                      const currentLot = getCurrentLot(auctionData.lots);
                      return currentLot?.likedUserIndexes;
                    })()}
                    onFavoriteUpdate={() => {}} // 소켓 데이터이므로 업데이트 안함
                    onLoginRequired={() => setShowLoginModal(true)}
                    onAdditionalInfoRequired={handleAdditionalInfoRequired}
                  />
                </ImageCarouselWrapper>
                <AuctionInfo 
                  auctionData={auctionDisplayData} 
                  showLiveStartPrice={true}
                />
              </DesktopLotContent>
            )}
            
            <MemoizedTitleRow 
              title={texts.liveAuction}
              onWatchLive={handleWatchLive}
              watchLiveText={texts.watchLive}
            />
            {auctionData?.url && (
              <YouTubePlayer youtubeUrl={auctionData.url} />
            )}
            <LotDescription description={(() => {
              if (!auctionData?.lots || auctionData.lots.length === 0) return '';
              const currentLot = getCurrentLot(auctionData.lots);
              return currentLot?.description || '';
            })()} />
            
            {/* 웹용 경매 카운트 */}
            <AuctionTimerWrapper>
              <AuctionTimer 
                currentPrice={currentPrice}
                exchangeRate={(() => {
                  if (!auctionData?.lots || auctionData.lots.length === 0) return 1300;
                  const currentLot = getCurrentLot(auctionData.lots);
                  return currentLot?.exchangeRate || 1300;
                })()}
                countdownStatus={countdownStatus} // 소켓에서 받은 카운트다운 상태 전달
                isWeb={true}
                onTimerClick={() => devLog('Timer clicked')}
              />
            </AuctionTimerWrapper>
            
            {/* 웹용 입찰 섹션 - 나의 입찰과 입찰 내역을 같은 행에 배치 */}
            <DesktopBidSection>
              <BidHistoryWrapper>
                <BidHistory bidHistory={transformedBidHistory} />
              </BidHistoryWrapper>
              <MyBidWrapper $hasBid={myBidInfo.hasBid}>
                <MyBid {...myBidInfo} isWeb={true} />
              </MyBidWrapper>
            </DesktopBidSection>
          </ContentContainer>
          
          {/* 고정 버튼 영역 - 데스크톱 */}
          <MemoizedFixedButtonContainer
            onClick={bidButtonProps.onClick}
            buttonText={bidButtonProps.buttonText}
          />
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <>
      <ResponsiveView
        mobileView={MobileLayout}
        desktopView={DesktopLayout}
      />
      
      {/* 경매 종료 모달 */}
      <AuctionEndedModal
        isOpen={showAuctionEndModal}
        onClose={handleAuctionEndConfirm}
        onConfirm={handleAuctionEndConfirm}
      />

      {/* 로그인 필요 모달 */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onLogin={handleLoginFromModal}
      />

      {/* 추가 정보 입력 모달 */}
      <AdditionalInfoModal
        isOpen={additionalInfoCheck.showAdditionalInfoModal}
        onClose={additionalInfoCheck.handleCloseModal}
        onGoToAdditionalInfo={additionalInfoCheck.handleGoToAdditionalInfo}
      />

      {/* 입찰 제한 모달 */}
      <BidRestrictedModal
        isOpen={showBidRestrictedModal}
        onClose={() => setShowBidRestrictedModal(false)}
      />
    </>
  );
}

export default LiveBid;
